// 能源圖表（plan reviews/energy-design-2026-10-21 DESIGN-NOTES §2、§4；kit.py bars() / lines() 的 React 版）：
//   - 軸、刻度、單位、標籤都是 HTML 文字（跟系統字級放大）；折線只有線本身是 SVG（non-scaling-stroke）
//   - 每格是可聚焦元素（role=img + aria-label 一句話；roving tabindex，←→ 移動、Home / End 跳頭尾），說明列 role=status
//   - 補估：同色淺一階 + 斜線；沒有資料：整欄高斜線背景 +「沒有資料」（不畫高度、不畫 0）；實測 0：基線短線；進行中：虛線框；未來不畫
//   - 圖下一定有〔看數字〕表（UX-22）；手機每格 < 44px 時另給〔‹ 前一格〕〔下一格 ›〕
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Quality } from './types';

/** 刻度：步距只准 1 / 2 / 2.5 / 5 × 10ⁿ，頂端 ≥ 最大值 × 1.05 的最小整齊刻度（設計稿 r4）。 */
export function niceScale(vmax: number, ndiv = 4): { step: number; top: number; ndiv: number } {
  const target = (vmax > 0 ? vmax : 1) * 1.05;
  let best: { step: number; top: number; ndiv: number } | null = null;
  for (const nd of [ndiv, ndiv - 1, ndiv + 1].filter((n) => n >= 2)) {
    const p = Math.pow(10, Math.floor(Math.log10(target / nd)));
    let found: { step: number; top: number; ndiv: number } | null = null;
    for (const mult of [1, 10, 100]) {
      for (const m of [1, 2, 2.5, 5]) {
        const step = m * p * mult;
        if (nd * step >= target) {
          found = { step, top: nd * step, ndiv: nd };
          break;
        }
      }
      if (found) break;
    }
    if (found && (!best || found.top < best.top - 1e-12)) best = found;
  }
  return best ?? { step: target / ndiv, top: target, ndiv };
}

/** 刻度字：同一軸補零對齊（0.05 / 0.10 / 0.15）。 */
export function tickLabels(step: number, ndiv: number, lang: string): string[] {
  const dec = Math.max(0, ...Array.from({ length: ndiv + 1 }, (_, k) => decimals(step * k)));
  const f = new Intl.NumberFormat(lang === 'zh-Hant' ? 'zh-TW' : lang === 'zh-Hans' ? 'zh-CN' : lang, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  return Array.from({ length: ndiv + 1 }, (_, k) => (k === 0 ? '0' : f.format(step * k)));
}

function decimals(v: number): number {
  const s = String(+v.toFixed(6));
  return s.includes('.') ? s.split('.')[1].length : 0;
}

export interface BarCell {
  /** null = 未來（不畫） */
  value: number | null;
  quality?: Quality;
  label: string;
  /** 朗讀與說明列的一句話（「14 時，0.42 度，實測」） */
  say: string;
  live?: boolean;
  prev?: number | null;
}

export interface NumTableData {
  head: string[];
  rows: (string | number)[][];
  total?: (string | number)[];
}

/** 〔看數字〕表。 */
export function NumTable({ data, summary, open }: { data: NumTableData; summary?: string; open?: boolean }) {
  const { t } = useTranslation();
  return (
    <details className="en-num" open={open}>
      <summary>{summary ?? t('energy:chart.numbers')}</summary>
      <table>
        <thead>
          <tr>
            {data.head.map((h, i) => (
              <th key={i} scope="col" className={i ? 'r' : undefined}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className={j ? 'r' : undefined}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {data.total && (
          <tfoot>
            <tr>
              {data.total.map((c, i) =>
                i === 0 ? (
                  <th key={i} scope="row">
                    {c}
                  </th>
                ) : (
                  <td key={i} className="r">
                    <b>{c}</b>
                  </td>
                ),
              )}
            </tr>
          </tfoot>
        )}
      </table>
    </details>
  );
}

export function Legend({ items }: { items: { k: string; text: string }[] }) {
  return (
    <ul className="en-legend">
      {items.map((it) => (
        <li key={it.k + it.text}>
          <i className={`lsw k-${it.k}`} aria-hidden />
          {it.text}
        </li>
      ))}
    </ul>
  );
}

function useRoving(n: number, initial: number) {
  const [cur, setCur] = useState(initial);
  useEffect(() => setCur(initial), [initial, n]);
  return [Math.min(Math.max(0, cur), Math.max(0, n - 1)), setCur] as const;
}

/** 直條圖。cells 的順序 = 時間順序；labelEvery = X 軸每幾格標一個字。 */
export function BarChart({
  title,
  unit,
  cells,
  height = 150,
  labelEvery = 1,
  table,
  stepLabels,
  ymax,
  testId,
  after,
}: {
  title: string;
  unit: string;
  cells: BarCell[];
  height?: number;
  labelEvery?: number;
  table: NumTableData;
  /** 〔‹ 前一天〕〔下一天 ›〕（手機每格太窄時給） */
  stepLabels?: [string, string];
  ymax?: number;
  testId?: string;
  after?: ReactNode;
}) {
  const { i18n, t } = useTranslation();
  const pid = useId();
  const area = useRef<HTMLDivElement>(null);
  const n = cells.length;
  const vals = cells.flatMap((c) => [c.value ?? 0, c.prev ?? 0]);
  const sc = useMemo(() => (ymax ? { step: ymax / 4, top: ymax, ndiv: 4 } : niceScale(Math.max(0, ...vals))), [ymax, vals.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const ticks = tickLabels(sc.step, sc.ndiv, i18n.language);
  const live = cells.findIndex((c) => c.live);
  const present = cells.map((c, i) => (c.value !== null || c.quality === 'incomplete' ? i : -1)).filter((i) => i >= 0);
  const initial = live >= 0 ? live : (present[present.length - 1] ?? 0);
  const [cur, setCur] = useRoving(n, initial);
  // 連續「沒有資料」的格併成一段
  const slots: { i: number; span: number; miss: boolean; fut: boolean }[] = [];
  for (let i = 0; i < n; ) {
    const c = cells[i];
    if (c.value === null && c.quality === 'incomplete' && !c.live && i < (present[present.length - 1] ?? -1) + 1) {
      let j = i;
      while (j + 1 < n && cells[j + 1].value === null && cells[j + 1].quality === 'incomplete' && present.includes(j + 1)) j++;
      slots.push({ i, span: j - i + 1, miss: true, fut: false });
      i = j + 1;
      continue;
    }
    slots.push({ i, span: 1, miss: false, fut: c.value === null });
    i++;
  }
  const focusables = slots.filter((s) => !s.fut).map((s) => s.i);
  const move = (to: number) => {
    const k = focusables.reduce((best, x) => (Math.abs(x - to) < Math.abs(best - to) ? x : best), focusables[0] ?? 0);
    setCur(k);
    requestAnimationFrame(() => area.current?.querySelector<HTMLElement>(`[data-i="${k}"]`)?.focus());
  };
  const onKey = (e: KeyboardEvent) => {
    const idx = focusables.indexOf(focusables.find((x) => x >= cur) ?? cur);
    if (e.key === 'ArrowRight') move(focusables[Math.min(focusables.length - 1, idx + 1)]);
    else if (e.key === 'ArrowLeft') move(focusables[Math.max(0, idx - 1)]);
    else if (e.key === 'Home') move(focusables[0]);
    else if (e.key === 'End') move(focusables[focusables.length - 1]);
    else return;
    e.preventDefault();
  };
  const curSlot = slots.find((s) => cur >= s.i && cur < s.i + s.span);
  const tipText = curSlot ? cells[curSlot.i].say : '';
  const fi = focusables.indexOf(curSlot?.i ?? -1);
  return (
    <div className="en-chartwrap" data-testid={testId}>
      <div className="en-plot" id={pid} role="group" aria-label={t('energy:chart.group', { title })} onKeyDown={onKey}>
        <div className="en-unit">{unit}</div>
        <div className="en-pg">
          <div className="en-y" aria-hidden style={{ ['--h' as string]: `${height}px` }}>
            <b className="sizer">{ticks.reduce((a, b) => (b.length > a.length ? b : a), '')}</b>
            {ticks.map((l, k) => (
              <span key={k} style={{ top: `${100 - (k * 100) / sc.ndiv}%` }}>
                {l}
              </span>
            ))}
          </div>
          <div className="en-area" ref={area} style={{ ['--h' as string]: `${height}px` }}>
            {ticks.map((_, k) => (
              <i key={k} className={`gl${k === 0 ? ' ax' : ''}`} style={{ top: `${100 - (k * 100) / sc.ndiv}%` }} />
            ))}
            <div className="en-bars" style={{ gridTemplateColumns: `repeat(${n},minmax(0,1fr))` }}>
              {slots.map((s) => {
                const c = cells[s.i];
                if (s.fut) return <span key={s.i} className="slot fut" aria-hidden />;
                if (s.miss)
                  return (
                    <span
                      key={s.i}
                      data-i={s.i}
                      className={`slot miss${cur === s.i ? ' sel' : ''}`}
                      style={{ gridColumn: `span ${s.span}` }}
                      role="img"
                      tabIndex={cur === s.i ? 0 : -1}
                      aria-label={c.say}
                      onClick={() => setCur(s.i)}
                    >
                      <em>{t('energy:q.incomplete')}</em>
                    </span>
                  );
                const v = c.value ?? 0;
                const cls = ['slot', c.quality === 'estimated' ? 'est' : '', c.live ? 'live' : '', cur === s.i ? 'sel' : '', v === 0 ? 'zero' : ''].filter(Boolean).join(' ');
                const h = v === 0 ? 0 : Math.max(1.5, (v / sc.top) * 100);
                return (
                  <span key={s.i} data-i={s.i} className={cls} role="img" tabIndex={cur === s.i ? 0 : -1} aria-label={c.say} onClick={() => setCur(s.i)}>
                    <i className="col" style={{ height: `${h}%` }} />
                    {c.prev !== undefined && c.prev !== null && <i className="prev" style={{ bottom: `${(c.prev / sc.top) * 100}%` }} />}
                  </span>
                );
              })}
            </div>
          </div>
          <span />
          <div className="en-x" aria-hidden>
            {cells.map((c, i) =>
              c.label && i % labelEvery === 0 ? (
                <span key={i} style={{ left: `${((i + 0.5) * 100) / n}%` }}>
                  {c.label}
                </span>
              ) : null,
            )}
          </div>
        </div>
      </div>
      {after}
      {tipText && (
        <div className="en-tip" role="status" aria-live="polite" data-testid={testId ? testId + '-tip' : undefined}>
          {tipText}
        </div>
      )}
      {stepLabels && focusables.length > 1 && (
        <div className="en-stepper">
          <button type="button" className="btn btn-sm" aria-controls={pid} aria-disabled={fi <= 0} onClick={() => fi > 0 && setCur(focusables[fi - 1])}>
            <span aria-hidden>‹</span> {stepLabels[0]}
          </button>
          <button type="button" className="btn btn-sm" aria-controls={pid} aria-disabled={fi >= focusables.length - 1} onClick={() => fi < focusables.length - 1 && setCur(focusables[fi + 1])}>
            {stepLabels[1]} <span aria-hidden>›</span>
          </button>
        </div>
      )}
      <NumTable data={table} />
    </div>
  );
}

export interface LineSeries {
  name: string;
  /** 1–5：系列色 --en-s1..s5 */
  color: number;
  values: (number | null)[];
}

/** 折線圖（比較{leaf}、7 天曲線、電池電量）：線尾直接標名稱（多條時依 y 避讓）。 */
export function LineChart({
  title,
  unit,
  series,
  labels,
  height = 150,
  labelEvery = 1,
  table,
  partialLast,
  testId,
  marks,
}: {
  title: string;
  unit: string;
  series: LineSeries[];
  labels: string[];
  height?: number;
  labelEvery?: number;
  table: NumTableData;
  partialLast?: boolean;
  testId?: string;
  /** 標記（例：套用功耗模式的時間）：位置 0–1 與字 */
  marks?: { x: number; text: string }[];
}) {
  const { i18n } = useTranslation();
  const n = labels.length;
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const sc = niceScale(Math.max(0, ...all), 4);
  const ticks = tickLabels(sc.step, sc.ndiv, i18n.language);
  const X = (i: number) => (n > 1 ? (i / (n - 1)) * 1000 : 0);
  const Y = (v: number) => 1000 - (v / sc.top) * 1000;
  const paths: ReactNode[] = [];
  const ends: { y: number; x: number; name: string; c: number }[] = [];
  series.forEach((s, si) => {
    const segs: [number, number][][] = [];
    let cur: [number, number][] = [];
    s.values.forEach((v, i) => {
      if (v === null) {
        if (cur.length) segs.push(cur);
        cur = [];
      } else cur.push([i, v]);
    });
    if (cur.length) segs.push(cur);
    const last = s.values.reduce<number>((a, v, i) => (v !== null ? i : a), -1);
    segs.forEach((sg, k) => {
      const pts = sg.map(([i, v]) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`);
      if (partialLast && sg[sg.length - 1][0] === last && pts.length >= 2) {
        if (pts.length > 2) paths.push(<polyline key={`${si}-${k}`} className={`ln s${s.color}`} vectorEffect="non-scaling-stroke" points={pts.slice(0, -1).join(' ')} />);
        paths.push(<polyline key={`${si}-${k}p`} className={`ln s${s.color} part`} vectorEffect="non-scaling-stroke" points={pts.slice(-2).join(' ')} />);
      } else paths.push(<polyline key={`${si}-${k}`} className={`ln s${s.color}`} vectorEffect="non-scaling-stroke" points={pts.join(' ')} />);
    });
    if (last >= 0) ends.push({ y: Y(s.values[last]!) / 10, x: X(last) / 10, name: s.name, c: s.color });
  });
  if (ends.length > 1) {
    ends.sort((a, b) => a.y - b.y);
    const gap = (22 / height) * 100;
    for (let k = 1; k < ends.length; k++) if (ends[k].y - ends[k - 1].y < gap) ends[k].y = ends[k - 1].y + gap;
  }
  return (
    <div className="en-chartwrap" data-testid={testId}>
      <div className={`en-plot line${series.length > 1 ? ' endpad' : ''}`} role="img" aria-label={title}>
        <div className="en-unit">{unit}</div>
        <div className="en-pg">
          <div className="en-y" aria-hidden style={{ ['--h' as string]: `${height}px` }}>
            <b className="sizer">{ticks.reduce((a, b) => (b.length > a.length ? b : a), '')}</b>
            {ticks.map((l, k) => (
              <span key={k} style={{ top: `${100 - (k * 100) / sc.ndiv}%` }}>
                {l}
              </span>
            ))}
          </div>
          <div className="en-area" style={{ ['--h' as string]: `${height}px` }}>
            {ticks.map((_, k) => (
              <i key={k} className={`gl${k === 0 ? ' ax' : ''}`} style={{ top: `${100 - (k * 100) / sc.ndiv}%` }} />
            ))}
            <svg className="en-lsvg" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden>
              {paths}
            </svg>
            {series.map((s) => {
              const last = s.values.reduce<number>((a, v, i) => (v !== null ? i : a), -1);
              return last < 0 ? null : (
                <i key={s.name} className={`dot s${s.color}${partialLast ? ' hollow' : ''}`} style={{ left: `${X(last) / 10}%`, top: `${Y(s.values[last]!) / 10}%` }} />
              );
            })}
            {series.length > 1 &&
              ends.map((e) => (
                <span key={e.name} className="endlab" style={{ left: `${e.x}%`, top: `${e.y}%` }}>
                  <i className={`lead s${e.c}`} />
                  {e.name}
                </span>
              ))}
            {marks?.map((m) => (
              <span key={m.text} className="mk" style={{ left: `${m.x * 100}%` }}>
                <b>{m.text}</b>
              </span>
            ))}
          </div>
          <span />
          <div className="en-x" aria-hidden>
            {labels.map((l, i) =>
              l && i % labelEvery === 0 ? (
                <span key={i} className={i === 0 ? 'x0' : i === n - 1 ? 'x1' : undefined} style={{ left: `${(X(i) / 10).toFixed(3)}%` }}>
                  {l}
                </span>
              ) : null,
            )}
          </div>
        </div>
      </div>
      <NumTable data={table} />
    </div>
  );
}

/** 24 小時迷你曲線（Station 每孔，共用刻度）。 */
export function Spark({ values, vmax, label, w = 96, h = 24 }: { values: (number | null)[]; vmax: number; label: string; w?: number; h?: number }) {
  const pts = values
    .map((v, i) => (v === null ? null : `${((w * i) / Math.max(1, values.length - 1)).toFixed(1)},${(h - 2 - ((h - 4) * v) / (vmax || 1)).toFixed(1)}`))
    .filter(Boolean)
    .join(' ');
  return (
    <svg className="en-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label}>
      <line className="base" x1="0" x2={w} y1={h - 1} y2={h - 1} />
      <polyline points={pts} />
    </svg>
  );
}
